import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  cloneAgent,
  updateAgentConfig,
  createAgent,
  getAgent,
  getAgents,
  getAgentTemplate,
  getAgentTemplates,
  updateAgent,
  getDataSet,
  updateDataSet,
  createDataSet,
  deleteDataSet,
  getDataSets,
  deleteAgent,
  getIndustries
} from './api';
import { useNavigate, useParams } from 'react-router-dom';
import { queryClient } from 'providers/AllProviders';
import { AgentResponse, IndustriesResponse } from './types';
import * as urls from 'routes/config';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { DatasetResponse } from '../setting/knowledgeBase/types';

export function useGetAgents(
  selectedWorkspaceId?: number,
  options?: UseQueryOptions<{ data: AgentResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId } = useParams();

  return useQuery<{ data: AgentResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agents', workspaceId, selectedWorkspaceId],
    queryFn: () => getAgents(workspaceId! ?? selectedWorkspaceId),
    enabled: Boolean(selectedWorkspaceId || workspaceId),
    ...options
  });
}

export function useGetIndustries(options?: UseQueryOptions<IndustriesResponse[], Error>) {
  return useQuery<IndustriesResponse[]>({
    queryKey: ['industries'],
    queryFn: () => getIndustries(),
    ...options
  });
}

export function useGetAgent(options?: UseQueryOptions<AgentResponse, Error>) {
  const { workspaceId, agentId } = useParams();

  return useQuery<AgentResponse>({
    queryKey: ['agent', workspaceId, agentId],
    queryFn: () => getAgent(workspaceId!, agentId!),
    enabled: Boolean(agentId && workspaceId),
    ...options
  });
}

export function useCreateAgent() {
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  return useMutation({
    mutationFn: (payload: FormData) => createAgent(workspaceId!, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['agents'],
        refetchType: 'active'
      });
      navigate(`${urls.agentBase(workspaceId, data.id)}/${urls.home.configure}`);
    }
  });
}

export function useUpdateAgent() {
  const { workspaceId, agentId } = useParams();

  return useMutation({
    mutationFn: (payload: any) => updateAgent(workspaceId!, agentId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agents', workspaceId],
        refetchType: 'all'
      });
      if (agentId) {
        queryClient.invalidateQueries({
          queryKey: ['agent', workspaceId, agentId],
          refetchType: 'all'
        });
      }
    }
  });
}

export function useDeleteAgent() {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useMutation({
    mutationFn: (id: number) => deleteAgent(workspaceId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agents', workspaceId],
        refetchType: 'active'
      });
    }
  });
}

export function useUpdateAgentConfig() {
  const { workspaceId, agentId } = useParams();

  return useMutation({
    mutationFn: (payload: any) => updateAgentConfig(workspaceId!, agentId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agent', workspaceId, agentId],
        refetchType: 'active'
      });
    }
  });
}

export function useGetAgentTemplates(
  params?: any,
  options?: UseQueryOptions<{ data: AgentResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId } = useParams();

  return useQuery<{ data: AgentResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agents', workspaceId, params],
    queryFn: () => getAgentTemplates(workspaceId!, params),
    ...options
  });
}

export function useGetAgentTemplate(id: string, options?: UseQueryOptions<AgentResponse, Error>) {
  const { workspaceId } = useParams();

  return useQuery<AgentResponse>({
    queryKey: ['agentTemplate', workspaceId],
    queryFn: () => getAgentTemplate(workspaceId!, id),
    enabled: Boolean(workspaceId),
    ...options
  });
}

export function useCloneAgent() {
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  return useMutation({
    mutationFn: (payload: { name?: string; website_url?: string; website_name?: string; agent_template_id?: number }) =>
      cloneAgent(workspaceId!, payload),
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({
          queryKey: ['agents', workspaceId],
          refetchType: 'all'
        });
        navigate(`${urls.agentBase(workspaceId, data.id)}/${urls.home.configure}`);
      }
    }
  });
}

export function useGetAgentDataSets(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: DatasetResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId, agentId } = useParams<{ workspaceId: string; agentId: string }>();

  return useQuery<{ data: DatasetResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agentDatasets', workspaceId, agentId, params],
    queryFn: () => getDataSets(workspaceId!, agentId!, params),
    enabled: Boolean(workspaceId && agentId),
    ...options
  });
}

export function useGetAgentDataSet(options?: UseQueryOptions<DatasetResponse, Error>) {
  const { workspaceId, agentId, datasetId } = useParams<{
    workspaceId: string;
    agentId: string;
    datasetId: string;
  }>();

  return useQuery<DatasetResponse>({
    queryKey: ['agentDataset', workspaceId, agentId, datasetId],
    queryFn: () => getDataSet(workspaceId!, agentId!, datasetId!),
    enabled: Boolean(workspaceId && agentId && datasetId),
    ...options
  });
}

export function useCreateOrUpdateAgentDataSet() {
  const navigate = useNavigate();
  const { workspaceId, agentId, datasetId } = useParams<{
    workspaceId: string;
    agentId: string;
    datasetId: string;
  }>();

  return useMutation({
    mutationFn: (payload: any) =>
      datasetId
        ? updateDataSet(workspaceId!, agentId!, datasetId!, payload)
        : createDataSet(workspaceId!, agentId!, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['agentDatasets', workspaceId, agentId],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', workspaceId, agentId],
        refetchType: 'active'
      });
      if (datasetId) {
        queryClient.invalidateQueries({
          queryKey: ['agentDataset', workspaceId, agentId, datasetId],
          refetchType: 'all'
        });
      }
      if (data !== undefined) {
        navigate(-1);
      }
    }
  });
}

export function useUpdateAgentDataSet() {
  const { workspaceId, agentId } = useParams<{
    workspaceId: string;
    agentId: string;
  }>();
  return useMutation({
    mutationFn: ({ datasetId, payload }: { datasetId: number; payload: any }) =>
      updateDataSet(workspaceId!, agentId!, String(datasetId!), payload),
    onSuccess: (data, variables) => {
      const { datasetId } = variables;
      queryClient.invalidateQueries({
        queryKey: ['agentDatasets', datasetId],
        refetchType: 'active'
      });
    }
  });
}

export function useDeleteAgentDataSet() {
  const { workspaceId, agentId } = useParams<{ workspaceId: string; agentId: string }>();

  return useMutation({
    mutationFn: (id: string) => deleteDataSet(workspaceId!, agentId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agentDatasets', workspaceId, agentId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', workspaceId, agentId],
        refetchType: 'active'
      });
    }
  });
}
